import {Stack} from '@mantine/core';
import React, {useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import InventoryStructuredTableMode from 'notebook/integrations/samples/InventoryStructuredTableMode';
import RowByRowSubmissionSetting, {
  RowOnlyConfig,
} from 'notebook/structured-tables/components/RowByRowSubmissionSetting';
import useSelectFolderOptionForTable, {
  StructuredTableFolderConfig,
} from 'notebook/structured-tables/components/useSelectFolderOptionForTable';
import {ButtonStyle} from 'ui-library/buttons/Button';
import Modal from 'ui-library/modals/Modal';

interface InventoryTableSettingsModalProps {
  locked: boolean;
  rowOnlyConfig?: RowOnlyConfig;
  insertMode: InventoryStructuredTableMode;
  folderConfig?: StructuredTableFolderConfig;
  onRequestClose: () => void;
  onRequestSubmit: (inventoryTableSettingsValues: InventoryTableSettingsModalValues) => void;
}

export interface InventoryTableSettingsModalValues {
  folderConfig?: StructuredTableFolderConfig;
  rowByRowSubmissionRequired?: boolean;
}

export default function InventoryTableSettingsModal(props: InventoryTableSettingsModalProps): JSX.Element {
  const showRowOnlySetting = props.rowOnlyConfig?.showSubmissionSetting;
  const [rowByRowSubmissionRequired, setRowByRowSubmissionRequired] = useState(
    props.rowOnlyConfig?.rowOnlyInitialValue ?? false
  );
  const {selectedFolderConfig, isInvalidFolderConfig, folderOptionSelector} = useSelectFolderOptionForTable({
    initialFolderConfig: props.folderConfig,
    itemTypeSupportsFolders: false,
    isReadOnly: props.locked,
    isForCreate: props.insertMode === InventoryStructuredTableMode.CREATE,
    projectIsRequired: BenchlingConfig.ENABLE_PROJECTS_REQUIRED_FOR_NON_LOCATION_STORABLES,
  });

  const handleRequestSubmit = (): void => {
    if (props.folderConfig != null || showRowOnlySetting) {
      props.onRequestSubmit({
        folderConfig: props.folderConfig != null ? selectedFolderConfig : undefined,
        ...(showRowOnlySetting ? {rowByRowSubmissionRequired} : {}),
      });
    }
  };

  return (
    <Modal
      title="Inventory table settings"
      onRequestClose={props.onRequestClose}
      onRequestSubmit={handleRequestSubmit}
      buttons={[
        {
          text: 'Save',
          disabled: props.locked || isInvalidFolderConfig,
          style: ButtonStyle.SECONDARY,
          submit: true,
        },
        {text: 'Cancel', cancel: true},
      ]}
    >
      <Stack spacing="sm">
        {props.folderConfig != null && folderOptionSelector}
        {showRowOnlySetting && (
          <RowByRowSubmissionSetting
            locked={props.locked}
            rowByRowSubmissionRequired={rowByRowSubmissionRequired}
            onRowByRowSubmissionRequiredChange={setRowByRowSubmissionRequired}
          />
        )}
      </Stack>
    </Modal>
  );
}
